// DataBreachInfo.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { apiFetch, BASE_URL } from "../api_call";
import { IoIosInformationCircle, IoIosClose } from "react-icons/io";
import { MdDelete, MdFileDownload } from "react-icons/md";
import "./Dashboard.css";

export default function DataBreachInfoTemp() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [successVisible, setSuccessVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // ---------------- Evidence Modal State ----------------
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null); // { task_id, ... }
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceDescription, setEvidenceDescription] = useState("");
  const [evidenceList, setEvidenceList] = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceSaving, setEvidenceSaving] = useState(false);
  const [evidenceError, setEvidenceError] = useState("");

  // ---------------- Fetch reports on load ----------------
  useEffect(() => {
    const taskNumber = localStorage.getItem("taskNumber");
    if (!taskNumber) {
      setErrorMsg("Report number not found.");
      setLoading(false);
      return;
    }

    const fetchReports = async () => {
      try {
        setLoading(true);
        setErrorMsg("");
        const token = localStorage.getItem("access_token");

        const res = await fetch(`${BASE_URL}/data-breach-temp/view/${taskNumber}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (res.status !== 200) throw data;

        // API returns array: data.tasks
        setReports(data.tasks || []);
      } catch (err) {
        console.error(err);
        setErrorMsg(err.error || "❌ Failed to fetch reports");
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  // ---------------- Delete All Reports ----------------
  const handleDeleteAll = async () => {
    const taskNumber = localStorage.getItem("taskNumber");
    if (!taskNumber) {
      alert("Report number not found");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete all reports for this task?"
    );
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${BASE_URL}/data-breach-temp/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ task_number: taskNumber }),
      });

      const data = await res.json();
      if (res.status !== 200) throw data;

      setSuccessMsg(data.message || "✅ All reports deleted successfully");
      setErrorMsg("");
      setReports([]); // clear table instantly
    } catch (err) {
      console.error(err);
      setErrorMsg(err.error || "❌ Failed to delete reports");
      setSuccessMsg("");
    }
  };

  // ---------------- Approver Email Change ----------------
  const handleApproverChange = (index, value) => {
    setReports((prev) => {
      const updated = [...prev];
      updated[index].approver_email = value;
      return updated;
    });
  };

  // ---------------- Send to Approver ----------------
  // Attachments no longer travel with this request — they're uploaded ahead
  // of time via the evidence modal and the backend pulls them from
  // attachment_data_breach for this task.
  const handleSendToApprover = async (report, index) => {
    if (!report.approver_email) {
      alert("Enter approver email");
      return;
    }

    if (!report.task_id) {
      alert("Task ID missing");
      return;
    }

    const formData = new FormData();
    formData.append("approver_email", report.approver_email);
    formData.append("task_id", report.task_id);

    try {
      const token = localStorage.getItem("access_token");

      const res = await fetch(`${BASE_URL}/data-breach-temp/send-to-approver`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (res.status !== 200) throw data;

      setSuccessMsg(
        data.emails_sent > 1
          ? `✅ Sent as ${data.emails_sent} emails (${data.attachments_sent} attachments)`
          : "✅ Sent to approver successfully"
      );
      setErrorMsg("");

      setReports((prev) => {
        const updated = [...prev];
        updated[index].task_status = "Requested";
        return updated;
      });
    } catch (err) {
      console.error(err);
      setErrorMsg(err.error || "❌ Failed to send to approver");
      setSuccessMsg("");
    }
  };

  // ---------------- Edit ----------------
  const handleEdit = (report) => {
    if (report.task_status?.toLowerCase() === "approved") {
      alert("Approved reports cannot be edited");
      return;
    }

    localStorage.setItem("taskId", report.task_id);
    localStorage.setItem("taskActionDate", report.task_action_date || "");
    localStorage.setItem("taskReminderDays", report.task_reminder_days || 1);
    localStorage.setItem("taskEscalationEmail", report.task_escalation_email || "");
    localStorage.setItem("taskEscalationReminderDays", report.task_escalation_reminder_days || 0);

    navigate("/data-breach");
  };

  // ---------------- Evidence Modal Handlers ----------------
  const openEvidenceModal = async (report, index) => {
    setActiveItem({ ...report, _index: index });
    setEvidenceFile(null);
    setEvidenceDescription("");
    setEvidenceError("");
    setEvidenceModalOpen(true);
    await fetchEvidenceList(report.task_id);
  };

  const closeEvidenceModal = () => {
    setEvidenceModalOpen(false);
    setActiveItem(null);
    setEvidenceFile(null);
    setEvidenceDescription("");
    setEvidenceList([]);
    setEvidenceError("");
  };

  const fetchEvidenceList = async (taskId) => {
    setEvidenceLoading(true);
    try {
      const res = await apiFetch(`/data-breach-temp/evidence/${taskId}`, {
        method: "GET",
      });
      setEvidenceList(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch evidence documents:", err);
      setEvidenceList([]);
    } finally {
      setEvidenceLoading(false);
    }
  };

  const handleSaveEvidence = async () => {
    if (!activeItem) return;

    if (!evidenceFile) {
      setEvidenceError("Please select a file to upload");
      return;
    }

    // Description is required — a document can't be saved without one,
    // so validate before ever hitting the API.
    const trimmedDescription = evidenceDescription.trim();
    if (!trimmedDescription) {
      setEvidenceError("Please add a description for this document");
      return;
    }

    const formData = new FormData();
    formData.append("task_id", activeItem.task_id);
    formData.append("description", trimmedDescription);
    // The raw File object already carries its original name and MIME type —
    // the backend reads both off it (file.filename / file.content_type) and
    // stores them in dbatc_name / dbatc_type, separate from dbatc_desc.
    formData.append("attachment", evidenceFile);

    setEvidenceSaving(true);
    setEvidenceError("");

    try {
      const res = await fetch(`${BASE_URL}/data-breach-temp/evidence/upload`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await res.json();
      if (res.status !== 200) throw data;

      setEvidenceFile(null);
      setEvidenceDescription("");

      await fetchEvidenceList(activeItem.task_id);
    } catch (err) {
      console.error(err);
      setEvidenceError(err?.error || "❌ Failed to upload document");
    } finally {
      setEvidenceSaving(false);
    }
  };

  const handleDeleteEvidence = async (docId) => {
    const confirmDelete = window.confirm("Delete this document?");
    if (!confirmDelete) return;

    try {
      await apiFetch(`/data-breach-temp/evidence/delete/${docId}`, {
        method: "DELETE",
      });

      setEvidenceList((prev) => prev.filter((doc) => doc.id !== docId));
    } catch (err) {
      console.error(err);
      setEvidenceError(err?.error || "❌ Failed to delete document");
    }
  };

  // The download route is auth-protected, so a plain <a href> won't carry
  // the token — fetch the blob ourselves and trigger the save using the
  // original filename (dbatc_name) so it comes back exactly as uploaded.
  const handleDownloadEvidence = async (doc) => {
    try {
      const res = await fetch(`${BASE_URL}${doc.download_url}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `evidence_${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setEvidenceError("❌ Failed to download document");
    }
  };

  // ---------------- Auto-hide Success ----------------
  useEffect(() => {
    if (successMsg) {
      setSuccessVisible(true);
      const timer = setTimeout(() => setSuccessVisible(false), 4500);
      const removeTimer = setTimeout(() => setSuccessMsg(""), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [successMsg]);

  const statusCounts = useMemo(() => {
    const counts = {
      total: 0,
      pending: 0,
      requested: 0,
      approved: 0,
      declined: 0,
    };

    reports.forEach((item) => {
      counts.total++;

      const status = item.task_status?.toLowerCase();

      if (status === "pending") counts.pending++;
      else if (status === "requested") counts.requested++;
      else if (status === "approved") counts.approved++;
      else if (status === "declined") counts.declined++;
    });

    return counts;
  }, [reports]);

  return (
    <div className="edit-compliance">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <main style={{ flex: 1, padding: "1rem 2rem", overflowY: "auto" }}>
          <header>
            <h2>Data Breach Reports</h2>
          </header>

          <div className="messages-top">
            {errorMsg && <div className="login-error">{errorMsg}</div>}
            {successMsg && (
              <div className={`success-msg ${!successVisible ? "fade-out" : ""}`}>
                {successMsg}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "10px",
            }}
          >
            <div style={{ display: "flex", gap: "10px" }}>
              <div style={{ background: "#e2e3e5", color: "#383d41", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Total: {statusCounts.total}
              </div>
              <div style={{ background: "#f8d7da", color: "#721c24", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Pending: {statusCounts.pending}
              </div>
              <div style={{ background: "#fff3cd", color: "#856404", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Requested: {statusCounts.requested}
              </div>
              <div style={{ background: "#d4edda", color: "#155724", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Approved: {statusCounts.approved}
              </div>
              <div style={{ background: "#ffb4b4", color: "#590a0a", padding: "6px 12px", borderRadius: "20px", fontWeight: "600", fontSize: "13px" }}>
                Declined: {statusCounts.declined}
              </div>
            </div>
          </div>

          {/* ---------------- Table ---------------- */}
          <div className="table-wrapper">
            <table className="data-table" style={{ tableLayout: "fixed", width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "180px" }}>ACTION</th>
                  <th style={{ width: "400px" }}>DESCRIPTION</th>
                  <th style={{ width: "100px" }}>START DATE</th>
                  <th style={{ width: "100px" }}>ACTION DATE</th>
                  <th style={{ width: "100px" }}>END DATE</th>
                  <th style={{ width: "90px" }}>REM. BEFORE</th>
                  <th style={{ width: "100px" }}>STATUS</th>
                  <th style={{ width: "120px" }}>APPROVER’S EMAIL</th>
                  <th style={{ width: "140px" }}>ATTACHMENTS</th>
                  <th style={{ width: "120px" }}>REQUEST APPROVAL</th>
                  <th style={{ width: "60px" }}>EDIT</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" style={{ textAlign: "center" }}>Loading...</td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{ textAlign: "center" }}>No reports found</td>
                  </tr>
                ) : (
                  reports.map((report, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span>{report.task_title || report.task_particular}</span>
                          {report.task_long_description && (
                            <IoIosInformationCircle
                              title={report.task_description}
                              style={{ cursor: "pointer", fontSize: "16px", color: "#1A1744" }}
                            />
                          )}
                        </div>
                      </td>

                      <td style={{ maxWidth: "400px" }}>
                        <div className="description-scroll">{report.task_long_description}</div>
                      </td>
                      <td>{report.task_start_date}</td>
                      <td>{report.task_action_date}</td>
                      <td>{report.task_end_date}</td>
                      <td>{report.task_reminder_days ? `${report.task_reminder_days} Days` : "-"}</td>
                      <td style={{
                        color:
                          report.task_status?.toLowerCase() === "approved"
                            ? "green"
                            : report.task_status?.toLowerCase() === "requested"
                            ? "orange"
                            : "red",
                        fontWeight: 500,
                      }}>
                        {report.task_status}
                      </td>

                      <td>
                        <input
                          type="email"
                          className="approver-input"
                          value={report.approver_email || ""}
                          onChange={(e) => handleApproverChange(index, e.target.value)}
                          placeholder="Enter email"
                        />
                      </td>

                      {/* ---------------- Attach Evidence -> opens modal ---------------- */}
                       <td style={{ width: "140px", maxWidth: "140px", overflow: "hidden", textAlign: "center" }}>
                        <button
                          type="button"
                          className="custom-file-btn"
                          onClick={() => openEvidenceModal(report, index)}
                          style={{
                            width: "auto",
                            maxWidth: "120px",
                            boxSizing: "border-box",
                            padding: "6px 14px",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            marginLeft: "4px",
                          }}
                        >
                          Attach Evidences
                        </button>
                      </td>

                      <td>
                        <button className="action-btn primary" onClick={() => handleSendToApprover(report, index)} title="Send to approver">
                          ✔
                        </button>
                      </td>

                      <td>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(report)}
                          disabled={report.task_status?.toLowerCase() === "approved"}
                          style={{
                            opacity: report.task_status?.toLowerCase() === "approved" ? 0.5 : 1,
                            cursor: report.task_status?.toLowerCase() === "approved" ? "not-allowed" : "pointer"
                          }}
                        >
                          ✎
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div style={{ textAlign: "center", marginTop: "25px" }}>
              <button
                className="submit-btn"
                style={{ width: "95%" }}
                onClick={handleDeleteAll}
              >
                Delete All
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* ---------------- Evidence Modal ---------------- */}
      {evidenceModalOpen && activeItem && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={closeEvidenceModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "10px",
              width: "640px",
              maxWidth: "90%",
              maxHeight: "85vh",
              overflowY: "auto",
              padding: "24px",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IoIosClose
              onClick={closeEvidenceModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                fontSize: "28px",
                cursor: "pointer",
                color: "#555",
              }}
            />

            <h3 style={{ marginTop: 0, marginBottom: "4px" }}>Attach Evidence</h3>
            <p style={{ margin: "0 0 16px", color: "#555", fontSize: "14px" }}>
              Report Number: <strong>{activeItem.task_number}</strong>
              {activeItem.task_title ? ` — ${activeItem.task_title}` : ""}
            </p>

            {evidenceError && (
              <div className="login-error" style={{ marginBottom: "12px" }}>
                {evidenceError}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
              <label className="file-upload-label">
                <input
                  type="file"
                  hidden
                  onChange={(e) => setEvidenceFile(e.target.files[0] || null)}
                />
                <span className="custom-file-btn">
                  {evidenceFile ? evidenceFile.name : "Choose File"}
                </span>
              </label>

              <label style={{ fontSize: "13px", fontWeight: 600, color: "#333" }}>
                Description <span style={{ color: "#c0392b" }}>*</span>
              </label>
              <textarea
                placeholder="Add a description for this document (e.g. signed invoice copy)"
                value={evidenceDescription}
                onChange={(e) => setEvidenceDescription(e.target.value)}
                required
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: evidenceDescription.trim() ? "1px solid #ccc" : "1px solid #c0392b",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />

              <button
                type="button"
                className="submit-btn"
                onClick={handleSaveEvidence}
                disabled={evidenceSaving || !evidenceFile || !evidenceDescription.trim()}
                style={{
                  alignSelf: "flex-start",
                  padding: "8px 20px",
                  opacity: evidenceSaving || !evidenceFile || !evidenceDescription.trim() ? 0.6 : 1,
                  cursor: evidenceSaving || !evidenceFile || !evidenceDescription.trim() ? "not-allowed" : "pointer",
                }}
              >
                {evidenceSaving ? "Saving..." : "Save"}
              </button>
            </div>

            <h4 style={{ marginBottom: "8px" }}>Previously Uploaded Documents</h4>

            {evidenceLoading ? (
              <p style={{ fontSize: "14px", color: "#777" }}>Loading...</p>
            ) : evidenceList.length > 0 ? (
              <table className="data-table" style={{ width: "100%", fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>File Name</th>
                    <th style={{ textAlign: "left" }}>Description</th>
                    <th style={{ width: "60px" }}></th>
                    <th style={{ width: "60px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {evidenceList.map((doc) => (
                    <tr key={doc.id}>
                      <td style={{ wordBreak: "break-all" }}>
                        {doc.filename || `evidence_${doc.id}`}
                      </td>
                      <td style={{ color: doc.description ? "inherit" : "#999" }}>
                        {doc.description || "—"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <MdFileDownload
                          onClick={() => handleDownloadEvidence(doc)}
                          style={{ cursor: "pointer", color: "#1A1744", fontSize: "18px" }}
                          title="Download document"
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <MdDelete
                          onClick={() => handleDeleteEvidence(doc.id)}
                          style={{ cursor: "pointer", color: "#c0392b", fontSize: "18px" }}
                          title="Delete document"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ fontSize: "14px", color: "#777" }}>No documents uploaded yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}